import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { displayName } from '../lib/format';
import { NAV_GROUPS, tabsForProfile } from '../lib/deskNav';
import LanguageSwitch from './LanguageSwitch';
import PoweredBy from './PoweredBy';

function NavItem({ link, t, onClick }) {
  return (
    <NavLink
      to={link.to}
      end={link.end}
      onClick={onClick}
      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
    >
      <span className="material-symbol" aria-hidden="true">
        {link.icon}
      </span>
      {t(link.labelKey)}
    </NavLink>
  );
}

export default function Layout() {
  const { profile, isManager, signOut } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = tabsForProfile(profile);
  const groups = NAV_GROUPS.map((id) => ({
    id,
    links: links.filter((link) => (link.group || 'office') === id),
  })).filter((group) => group.links.length);
  const workshopLinks = links.filter((link) => link.group === 'workshop').slice(0, 4);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const roleLabel = isManager ? t('common.administrator') : t(`role.${profile?.role}`) || profile?.role;

  return (
    <div className={`shell${menuOpen ? ' menu-open' : ''}`}>
      <header className="topbar">
        <NavLink to={links[0]?.to || '/'} className="rail-brand" end>
          <strong>{t('layout.workshop')}</strong>
          <span>{t('layout.serviceDesk')}</span>
        </NavLink>
        <button
          type="button"
          className="topbar-menu"
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="material-symbol" aria-hidden="true">
            {menuOpen ? 'close' : 'menu'}
          </span>
          {t('nav.menu')}
        </button>
      </header>

      <aside className="rail" id="main-nav" aria-label="Main navigation">
        <NavLink to={links[0]?.to || '/'} className="rail-brand rail-brand-desk" end>
          <strong>{t('layout.workshop')}</strong>
          <span>{t('layout.serviceDesk')}</span>
        </NavLink>
        <nav>
          {groups.map((group) => (
            <div key={group.id} className={`nav-group nav-group-${group.id}`}>
              {groups.length > 1 ? <span className="nav-group-label">{t(`navgroup.${group.id}`)}</span> : null}
              {group.links.map((link) => (
                <NavItem key={link.id} link={link} t={t} />
              ))}
            </div>
          ))}
        </nav>
        <div className="rail-foot">
          <LanguageSwitch />
          <div className="who">
            <span>{displayName(profile)}</span>
            <small>{roleLabel}</small>
          </div>
          <button type="button" className="ghost" onClick={handleSignOut}>
            {t('common.signOut')}
          </button>
        </div>
      </aside>
      {menuOpen ? <div className="rail-scrim" onClick={() => setMenuOpen(false)} role="presentation" /> : null}

      <main className="desk">
        <div className="desk-body">
          <Outlet />
        </div>
        <PoweredBy />
      </main>

      {workshopLinks.length > 1 ? (
        <nav className="tabbar" aria-label={t('navgroup.workshop')}>
          {workshopLinks.map((link) => (
            <NavItem key={link.id} link={link} t={t} />
          ))}
        </nav>
      ) : null}
    </div>
  );
}
